import { makeVar, useApolloClient, useReactiveVar } from '@apollo/client'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { generateTextFromRichTextEditorContent } from '~common/RichTextEditor'
import { LaunchDocument } from '~generated/hooks'
import { LaunchQuery } from '~generated/operations'
import { CopysmithPromptsType } from '~generated/types'
import Modal from '~lib/ui-kit/Modal'

export interface CopilotModalState {
  heading: string
  description?: string
  gptPrompt?: CopysmithPromptsType
  onCompleted?: (text: string) => void
  onClose?: () => void
}

export const copilotCopyWithAIModalHandler = Modal.createModalHandlerReactiveVar()
export const copilotCopyWithAIModalStateVar = makeVar<CopilotModalState>(null)
export const copilotCopyWithAIModalTextVar = makeVar('')

export const useModalStateVar = () => {
  const state = useReactiveVar(copilotCopyWithAIModalStateVar)
  return { ...(state || ({} as CopilotModalState)) }
}

/**
 * These two modules content is used to initially fill in description for copy and assets module.
 */
const MESSAGING_MODULE_NAME = 'Messaging'
const LAUNCH_SUMMARY_MODULE_NAME = 'Launch Summary'

export const useCopilotCopyWithAIModalText = (initialText = '') => {
  const apollo = useApolloClient()
  const router = useRouter()
  const launchId = router.query.launchId as string

  /**
   * Copy and Assets generate wi AI take intial description content from Messaging or Summary modules.
   * For other modules where generate with AI suppuse to be initial content is still unknown, thats why this container is only Copy and Assets logic
   */
  const [text, setText] = useState(() => {
    if (initialText) return initialText

    const data = apollo.cache.readQuery<LaunchQuery>({
      query: LaunchDocument,
      variables: { id: launchId },
    })

    const modules = (data?.launch.launchSections || []).flatMap(({ launchModules }) => launchModules)

    const requiredModule =
      modules.find(({ name }) => name === MESSAGING_MODULE_NAME) ||
      modules.find(({ name }) => name === LAUNCH_SUMMARY_MODULE_NAME)

    // Both messaging and launch summary modules have WYSIWYG editor, thats why their user input is just as string
    const editorContent = requiredModule?.userInput[requiredModule.editor] as string

    if (editorContent) {
      return generateTextFromRichTextEditorContent(editorContent)
    }

    return ''
  })

  return [text, setText] as const
}
